import { useCallback, useEffect, useState } from "react";
import { adminSeoStudio, adminUpdateSeoSettings, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  MagicWand, Check, X as XIcon, Warning, Robot,
  Link as LinkIcon, Image as ImageIcon, FilePdf,
  MagnifyingGlass, BookOpen,
  InstagramLogo, YoutubeLogo, LinkedinLogo, FacebookLogo, RedditLogo,
  TiktokLogo, PinterestLogo, XLogo,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import AdminCard from "../components/AdminCard";
import AdminBadge from "../components/AdminBadge";
import AdminPrimaryButton from "../components/AdminPrimaryButton";
import AdminToggle from "../components/AdminToggle";

const ALL_SEO_PLATFORMS = [
  { id: "google",    label: "Google SEO",  Icon: MagnifyingGlass, color: "#4285F4" },
  { id: "reddit",    label: "Reddit",      Icon: RedditLogo,      color: "#FF4500" },
  { id: "x",         label: "X (Twitter)", Icon: XLogo,           color: "#0F172A" },
  { id: "instagram", label: "Instagram",   Icon: InstagramLogo,   color: "#E1306C" },
  { id: "facebook",  label: "Facebook",    Icon: FacebookLogo,    color: "#1877F2" },
  { id: "linkedin",  label: "LinkedIn",    Icon: LinkedinLogo,    color: "#0A66C2" },
  { id: "youtube",   label: "YouTube",     Icon: YoutubeLogo,     color: "#FF0000" },
  { id: "tiktok",    label: "TikTok",      Icon: TiktokLogo,      color: "#010101" },
  { id: "pinterest", label: "Pinterest",   Icon: PinterestLogo,   color: "#E60023" },
  { id: "medium",    label: "Medium",      Icon: BookOpen,        color: "#00AB6C" },
];

function SeoStatCard({ label, value, sub, color = "#4F46E5" }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 block">{label}</span>
      <span className="text-2xl font-bold mt-2 block" style={{ color }}>{value}</span>
      {sub && <span className="text-xs text-slate-400 mt-0.5 block">{sub}</span>}
    </div>
  );
}

function ToggleRow({ icon: Icon, iconColor, title, description, badge, enabled, onToggle, testId }) {
  return (
    <div className="flex items-center justify-between px-6 py-5 gap-4 border-b border-slate-100 last:border-b-0">
      <div className="flex items-center gap-3">
        <Icon size={18} style={{ color: enabled ? iconColor : "#CBD5E1" }} />
        <div>
          <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            {title}
            {badge && (
              <span className="bg-violet-50 border border-violet-200 text-violet-600 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full">
                {badge}
              </span>
            )}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">{description}</p>
        </div>
      </div>
      <AdminToggle
        enabled={enabled}
        onToggle={onToggle}
        size={40}
        data-testid={testId}
        aria-label={title}
      />
    </div>
  );
}

export default function SeoStudioAdminTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [studioEnabled, setStudioEnabled] = useState(true);
  const [urlScrapingEnabled, setUrlScrapingEnabled] = useState(true);
  const [imageUploadEnabled, setImageUploadEnabled] = useState(false);
  const [documentUploadEnabled, setDocumentUploadEnabled] = useState(false);
  const [enabledPlatforms, setEnabledPlatforms] = useState(ALL_SEO_PLATFORMS.map((p) => p.id));
  const [freeQuota, setFreeQuota] = useState(5);
  const [freeCharLimit, setFreeCharLimit] = useState(500);
  const [freeKwLimit, setFreeKwLimit] = useState(5);
  const [freePlatformLimit, setFreePlatformLimit] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const d = await adminSeoStudio();
      setData(d);
      const s = d.settings;
      setStudioEnabled(s.seo_studio_enabled);
      setUrlScrapingEnabled(s.seo_url_scraping_enabled);
      setImageUploadEnabled(s.seo_image_upload_enabled);
      setDocumentUploadEnabled(s.seo_document_upload_enabled);
      setEnabledPlatforms(s.seo_enabled_platforms || ALL_SEO_PLATFORMS.map((p) => p.id));
      setFreeQuota(s.seo_free_daily_quota);
      setFreeCharLimit(s.seo_free_char_limit);
      setFreeKwLimit(s.seo_free_keyword_limit);
      setFreePlatformLimit(s.seo_free_platform_limit);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePlatform = (id) => {
    setEnabledPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      await adminUpdateSeoSettings({
        seo_studio_enabled: studioEnabled,
        seo_url_scraping_enabled: urlScrapingEnabled,
        seo_image_upload_enabled: imageUploadEnabled,
        seo_document_upload_enabled: documentUploadEnabled,
        seo_enabled_platforms: enabledPlatforms,
        seo_free_daily_quota: freeQuota,
        seo_free_char_limit: freeCharLimit,
        seo_free_keyword_limit: freeKwLimit,
        seo_free_platform_limit: freePlatformLimit,
      });
      toast.success("SEO Studio settings saved");
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="text-slate-400 text-sm">Loading…</p>;

  const maxDaily = data ? Math.max(1, ...data.daily_trend.map((d) => d.count)) : 1;

  return (
    <div className="space-y-8 max-w-5xl" data-testid="seo-studio-admin-tab">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <MagicWand size={16} className="text-indigo-600" weight="fill" />
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">SEO Studio · Admin</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-900">SEO Studio Control</h2>
          <p className="text-sm text-slate-500 mt-1">Analytics, feature toggles, platform controls, and free-tier limits.</p>
        </div>
        <AdminBadge variant={studioEnabled ? "success" : "error"}>
          {studioEnabled ? <Check size={10} weight="bold" /> : <XIcon size={10} weight="bold" />}
          Studio {studioEnabled ? "Live" : "Offline"}
        </AdminBadge>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center gap-2">
          <Warning size={14} /> {err}
        </div>
      )}

      {/* Analytics */}
      {data && (
        <section className="space-y-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Analytics</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SeoStatCard label="Total generations" value={data.total_generations} color="#4F46E5" />
            <SeoStatCard label="Avg SEO score" value={data.avg_score} sub="out of 100" color="#059669" />
            <SeoStatCard
              label="Top platform"
              value={data.top_platforms[0]?.platform?.toUpperCase() ?? "—"}
              sub={`${data.top_platforms[0]?.count ?? 0} uses`}
              color="#7C3AED"
            />
            <SeoStatCard
              label="Active platforms"
              value={enabledPlatforms.length}
              sub={`of ${ALL_SEO_PLATFORMS.length} total`}
              color="#D97706"
            />
          </div>

          {/* Daily trend chart */}
          <AdminCard>
            <div className="p-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-5">Generations · last 7 days</p>
              <div className="flex items-end gap-3 h-32">
                {data.daily_trend.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1">
                    <div
                      className="w-full bg-indigo-400 hover:bg-indigo-500 transition-colors rounded-t-sm"
                      style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: d.count > 0 ? "4px" : "0" }}
                      title={`${d.date}: ${d.count}`}
                    />
                    <div className="text-[9px] text-slate-400 uppercase">{d.date.slice(5)}</div>
                    <div className="text-[10px] font-bold text-slate-600">{d.count}</div>
                  </div>
                ))}
              </div>
            </div>
          </AdminCard>

          {/* Platform usage */}
          {data.top_platforms.length > 0 && (
            <AdminCard header="Platform usage">
              <div className="p-6 space-y-3">
                {data.top_platforms.map(({ platform, count }) => {
                  const cfg = ALL_SEO_PLATFORMS.find((p) => p.id === platform);
                  const pct = Math.round((count / (data.total_generations || 1)) * 100);
                  const Icon = cfg?.Icon ?? MagicWand;
                  return (
                    <div key={platform} className="flex items-center gap-3">
                      <Icon size={13} style={{ color: cfg?.color ?? "#94A3B8" }} />
                      <span className="text-xs font-medium text-slate-500 w-20 truncate">{cfg?.label ?? platform}</span>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: cfg?.color ?? "#4F46E5" }}
                        />
                      </div>
                      <span className="text-xs text-slate-400 w-8 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </AdminCard>
          )}

          {/* Recent generations */}
          {data.recent.length > 0 && (
            <AdminCard header="Recent generations">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      {["Title", "Platforms", "Score", "Date"].map((h) => (
                        <th key={h} className="px-5 py-2.5 text-left text-[9px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                        <td className="px-5 py-3 text-slate-700 max-w-xs truncate">{r.seoTitle || "—"}</td>
                        <td className="px-5 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(r.platforms || []).map((p) => {
                              const cfg = ALL_SEO_PLATFORMS.find((x) => x.id === p);
                              return (
                                <span
                                  key={p}
                                  className="px-1.5 py-0.5 text-[9px] font-semibold rounded-full border"
                                  style={{ borderColor: `${cfg?.color ?? "#94A3B8"}50`, color: cfg?.color ?? "#94A3B8" }}
                                >
                                  {p}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <span
                            className="font-bold text-sm"
                            style={{ color: r.seoScore >= 80 ? "#059669" : r.seoScore >= 60 ? "#D97706" : "#DC2626" }}
                          >
                            {r.seoScore ?? "—"}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-slate-400">
                          {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </AdminCard>
          )}
        </section>
      )}

      {/* Studio controls */}
      <section className="space-y-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Studio controls</p>
        <AdminCard>
          <ToggleRow
            icon={Robot} iconColor="#4F46E5"
            title="SEO Studio"
            description="Master switch — turns off the entire /seo-studio page for all users."
            enabled={studioEnabled}
            onToggle={() => setStudioEnabled((v) => !v)}
            testId="toggle-seo-studio"
          />
          <ToggleRow
            icon={LinkIcon} iconColor="#3B82F6"
            title="URL Content Scraping"
            description="Allow users to paste a source URL to auto-extract page content for AI context."
            enabled={urlScrapingEnabled}
            onToggle={() => setUrlScrapingEnabled((v) => !v)}
            testId="toggle-url-scraping"
          />
          <ToggleRow
            icon={ImageIcon} iconColor="#7C3AED"
            title="Image Upload Context"
            description="Let users upload images to include as context for SEO generation."
            badge="Coming soon"
            enabled={imageUploadEnabled}
            onToggle={() => setImageUploadEnabled((v) => !v)}
            testId="toggle-image-upload"
          />
          <ToggleRow
            icon={FilePdf} iconColor="#D97706"
            title="Document Upload Context"
            description="Let users upload PDFs or text files to feed as content context for SEO generation."
            badge="Coming soon"
            enabled={documentUploadEnabled}
            onToggle={() => setDocumentUploadEnabled((v) => !v)}
            testId="toggle-document-upload"
          />
        </AdminCard>
      </section>

      {/* Platform controls */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Platform controls</p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setEnabledPlatforms(ALL_SEO_PLATFORMS.map((p) => p.id))}
              className="text-xs font-medium text-slate-500 hover:text-indigo-600 transition-colors"
            >
              Enable all
            </button>
            <span className="text-slate-300">·</span>
            <button
              onClick={() => setEnabledPlatforms([])}
              className="text-xs font-medium text-slate-500 hover:text-red-600 transition-colors"
            >
              Disable all
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-400">Disabled platforms are hidden from the user platform picker and rejected server-side.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ALL_SEO_PLATFORMS.map(({ id, label, Icon, color }) => {
            const on = enabledPlatforms.includes(id);
            return (
              <div
                key={id}
                className={`flex items-center justify-between border rounded-xl px-5 py-4 transition-all ${
                  on ? "border-slate-200 bg-white shadow-sm" : "border-slate-100 bg-slate-50 opacity-60"
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon size={16} style={{ color: on ? color : "#CBD5E1" }} />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{label}</p>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">{id}</p>
                  </div>
                </div>
                <AdminToggle
                  enabled={on}
                  onToggle={() => togglePlatform(id)}
                  size={36}
                  data-testid={`toggle-platform-${id}`}
                  aria-label={`Toggle ${label}`}
                />
              </div>
            );
          })}
        </div>

        {enabledPlatforms.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 text-sm text-amber-700">
            <Warning size={14} />
            No platforms enabled — users will not be able to generate any SEO content.
          </div>
        )}
      </section>

      {/* Free tier limits */}
      <section className="space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Free tier limits</p>
          <p className="text-xs text-slate-400 mt-1">These limits apply to free users. Premium users always get full access.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { label: "Daily generation quota", hint: "Max SEO generations per day for free users.", value: freeQuota, onChange: setFreeQuota, min: 1, max: 100, presets: [3, 5, 10, 20], testId: "input-free-quota" },
            { label: "Max content characters", hint: "Max characters in the description field for free users.", value: freeCharLimit, onChange: setFreeCharLimit, min: 100, max: 5000, step: 100, presets: [300, 500, 1000, 2000], testId: "input-free-char-limit" },
            { label: "Max keywords", hint: "Maximum number of target keywords free users can provide.", value: freeKwLimit, onChange: setFreeKwLimit, min: 1, max: 25, presets: [3, 5, 8, 12], testId: "input-free-kw-limit" },
            { label: "Max platforms (free)", hint: "How many platforms free users can select at once.", value: freePlatformLimit, onChange: setFreePlatformLimit, min: 1, max: 10, presets: [1, 2, 3, 5], testId: "input-free-platform-limit" },
          ].map(({ label, hint, value, onChange, min, max, step, presets, testId }) => (
            <AdminCard key={testId}>
              <div className="p-5 space-y-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{hint}</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <input
                    type="number"
                    min={min} max={max} step={step}
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    className="w-20 bg-white border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none rounded-lg px-3 py-2 text-sm text-slate-900 text-center"
                    data-testid={testId}
                  />
                  <div className="flex gap-1.5">
                    {presets.map((v) => (
                      <button
                        key={v}
                        onClick={() => onChange(v)}
                        className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                          value === v
                            ? "bg-indigo-600 border-indigo-600 text-white"
                            : "border-slate-300 text-slate-500 hover:border-indigo-400 hover:text-indigo-600"
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </AdminCard>
          ))}
        </div>
      </section>

      {/* Save */}
      <div className="flex items-center gap-3 pt-2 border-t border-slate-200">
        <AdminPrimaryButton
          onClick={save}
          busy={busy}
          busyLabel="Saving…"
          icon={<MagicWand size={14} weight="bold" />}
          data-testid="seo-admin-save"
        >
          Save SEO settings
        </AdminPrimaryButton>
        <button
          onClick={load}
          disabled={loading || busy}
          className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-slate-600 font-medium px-4 py-2.5 text-sm rounded-lg transition-colors"
        >
          <ArrowsClockwise size={13} /> Refresh
        </button>
      </div>
    </div>
  );
}
